import {
	AG_CHARTS_LOCALE_EN_US,
	AG_CHARTS_LOCALE_FI_FI,
	AG_CHARTS_LOCALE_NL_NL,
	AG_CHARTS_LOCALE_SV_SE,
} from 'ag-charts-locale'
import i18n from 'i18next'
import { useEffect, useRef } from 'react'
import { countryCodesGet, tagManagerSearch } from '../api'
import { supportedLanguages } from '../i18n'
import { gridSettings } from './grid'

if (import.meta.env.PROD) {
	// disable console logs in production, except jsError which sends errors to backend
	//console.log = () => {};
	//console.info = () => {};
	//console.warn = () => {};
	//console.error = () => {};
	import('./jserror')
}

export const S3_IMAGES_URL = import.meta.env.VITE_STORAGE_S3_IMAGES_URL ?? ''

export const NOTIFICATIONS_ENABLED = import.meta.env.PROD || import.meta.env.VITE_NOTIFICATIONS_DISABLED !== 'true'

// close a Popover by clicking outside
export const closePopover = () => document.body.click()

export const capitalizeFirstLetter = (str) => str.charAt(0).toUpperCase() + str.slice(1)
export const lowerCaseFirstLetter = (str) => str.charAt(0).toLowerCase() + str.slice(1)

export const personOption = ({ personId, personNumber, personName, ended }, t, usePersonNumberAsValue) => {
	const value = usePersonNumberAsValue ? personNumber : personId
	const numberName = `${personNumber}. ${personName}`
	const label = ended ? (
		<small className="ended">
			{numberName} ({t ? t('ended', 'päättynyt') : 'päättynyt'})
		</small>
	) : (
		numberName
	)
	return { value, label, numberName }
}

/**
 * Profile photo URL from eSalary if image includes a dot for .png/.jpg or from S3 with imagekey-_ otherwise
 * @param {string} eSalaryCompanyId auth.eSalaryCompanyId
 * @param {string} image filename
 * @returns {string} URL
 */
export const eSalaryImage = (eSalaryCompanyId, image) =>
	image
		? image.includes('.')
			? `https://phantasos.heeros.com:8025/images/${eSalaryCompanyId}/${image}`
			: S3_IMAGES_URL + image
		: null

/**
 * Logo URL from S3 or eSalary
 * @param {string} companyLogoUrl auth.companyLogoUrl from eSalary
 * @param {string} organizationLogoUrl auth.company?.logoUrl from OrganizationData (S3)
 * @returns {string} URL
 */
export const logoUrl = (companyLogoUrl, organizationLogoUrl) =>
	organizationLogoUrl
		? S3_IMAGES_URL + organizationLogoUrl
		: companyLogoUrl
			? `https://epalkat.heeros.com/${companyLogoUrl}` // images/QECKAWWWNKSJWZSMOLLP/Heavy Metalli.png
			: ''

const agChartsLocales = {
	fi: AG_CHARTS_LOCALE_FI_FI,
	sv: AG_CHARTS_LOCALE_SV_SE,
	nl: AG_CHARTS_LOCALE_NL_NL,
	en: AG_CHARTS_LOCALE_EN_US,
}

/**
 * @typedef LocalizedProperties
 * @type {object}
 * @property {import("ag-grid-enterprise").AgLocaleOptions} agChartsLocale
 * @property {Intl.NumberFormat} numberFormatFraction1
 */
/** @type {LocalizedProperties} */
export const localized = {}
i18n.on('initialized', () => {
	const { t } = i18n
	document.documentElement.lang = i18n.resolvedLanguage // update html lang attribute
	localized.resolvedLanguage = i18n.resolvedLanguage === 'en' ? 'en-GB' : i18n.resolvedLanguage
	localized.languageName = new Intl.DisplayNames(localized.resolvedLanguage, {
		type: 'language',
	})
	localized.languageOptions = supportedLanguages.map((lng) => (
		<option key={lng} value={lng}>
			{localized.languageName.of(lng)}
		</option>
	))
	localized.countryName = new Intl.DisplayNames(localized.resolvedLanguage, {
		type: 'region',
	})
	localized.numberFormat = new Intl.NumberFormat(localized.resolvedLanguage)
	localized.numberFormatFraction = new Intl.NumberFormat(localized.resolvedLanguage, {
		minimumFractionDigits: 2,
		maximumFractionDigits: 2,
	})
	localized.numberFormatFraction1 = new Intl.NumberFormat(localized.resolvedLanguage, {
		minimumFractionDigits: 1,
		maximumFractionDigits: 1,
	})
	localized.currencyFormat = new Intl.NumberFormat(localized.resolvedLanguage, {
		style: 'currency',
		currency: 'EUR',
	})
	//localized.premiumAlert = buildAlertWithLocalizedMessage("WARNING", "PremiumTitle", "💎 Premium", "PremiumDescription", "Tämä toiminto on saatavilla Premium-versiossa.", { premiumLink: true });
	localized.dateFormat =
		i18n.resolvedLanguage === 'en' ? 'dd/mm/yyyy' : i18n.resolvedLanguage === 'sv' ? 'yyyy-mm-dd' : 'd.m.yyyy' // Excel export date format
	localized.agChartsLocale = {
		localeText: agChartsLocales[i18n.resolvedLanguage],
	} // AgLocaleOptions https://www.ag-grid.com/charts/react/localisation/

	gridSettings.excelStyles[0].numberFormat.format = localized.dateFormat // update AG Grid Excel export date format

	localized.employmentTypes = {
		0: t('Choose', 'Valitse'),
		1: t('Fulltime', 'Kokoaikainen'),
		2: t('Parttime', 'Osa-aikainen'),
		3: t('NoInfo', 'Ei tietoa'),
	}

	localized.salaryForms = {
		0: t('Choose', 'Valitse'),
		1: t('MonthlyWage', 'Kuukausipalkka'),
		2: t('HourlyWage', 'Aikatuntipalkka'),
		3: t('IncentiveWage', 'Urakkapalkka'),
	}

	localized.personalBenefit = {
		0: t('Choose', 'Valitse'),
		1: t('CarBenefit', 'Autoetu'),
		2: t('AccommodationBenefit', 'Asuntoetu'),
		3: t('Garagebenefit', 'Autotallietu'),
		4: t('FreeAccommodationBenefit', 'Täysihoitoetu'),
	}
})

export function generateOptions(obj) {
	const options = []
	for (const key in obj) {
		options.push(
			<option key={key} value={key}>
				{obj[key]}
			</option>,
		)
	}
	return options
}

let countryOptions
export async function getCountryOptions(setCountryOptions) {
	if (countryOptions === undefined) {
		const countryResponse = await countryCodesGet()
		const countries = countryResponse.data.map((code) => [localized.countryName.of(code), code]).sort() // localeCompare()
		countries.push(['Tuntematon', '99'])
		countryOptions = countries.map((arr) => ({
			value: arr[1],
			label: `${arr[1]} - ${arr[0]}`,
			name: arr[0],
		}))
	}
	setCountryOptions(countryOptions)
}

// return value from data according to current AG Grid colDef.field
//export const fieldGetter = ({ colDef, data }) => {
//  if (!data || !colDef.field) return null;
// https://stackoverflow.com/questions/8051975/access-object-child-properties-using-a-dot-notation-string
//  return colDef.field.split(".").reduce((a, b) => (a !== null ? a[b] : null), data);
//};

export const fieldGetter = ({ colDef, data }) => {
	if (!data || !colDef.field) return null

	return colDef.field.split('.').reduce((a, b) => (a != null ? a[b] : null), data)
}

export const dateGetter = (params) => {
	const value = fieldGetter(params)
	if (value !== null && value !== 0) {
		const date = new Date(value)
		date.setHours(0, 0, 0, 0) // required for ag-grid equals filtering (was setUTCHours)
		return Number.isNaN(date.getDate()) ? null : date
	}
	return null
}

const dateRE = /(\d+)\.(\d+)\.(\d+)/ // 1.06.2023
export const eSalaryDateGetter = ({ colDef, data }) => {
	if (!data) return null
	const value = data[colDef.field] // const dmy = data[colDef.field].split(".");
	if (!value) return null
	const matches = value.match(dateRE)
	if (matches !== null) {
		const date = new Date(`${matches[3]}-${matches[2]}-${matches[1]}`)
		date.setHours(0) // required for ag-grid equals filtering
		return date
	} else {
		return dateGetter({ colDef, data }) // fallback to ISO format
	}
}

export const dateFormatter = ({ value }) => value?.toLocaleDateString?.(localized.resolvedLanguage)
export const starFormatter = ({ value }) => '⭐'.repeat(value)
export const emptyFormatter = () => ''
export const numberFormatter = ({ value }) =>
	value ? (localized.numberFormat ? localized.numberFormat.format(value) : value) : ''
export const numberFormatterFraction = ({ value }) =>
	value ? (localized.numberFormatFraction ? localized.numberFormatFraction.format(value) : value) : ''
export const hideZeroFormatter = ({ value }) => (value ? value : '')

export const average = (arr) => (arr.length ? arr.reduce((sum, a) => sum + a, 0) / arr.length : undefined)
export const median = (arr) => {
	if (!arr.length) return undefined

	const sorted = [...arr].sort((a, b) => a - b)
	const middle = Math.floor(sorted.length / 2)

	return sorted.length % 2 ? sorted[middle] : average(sorted.slice(middle - 1, middle + 1))
}

/*
export const formatCurrentDate = (currentDate = new Date()) => {
	const year = currentDate.getFullYear();
	const month = String(currentDate.getMonth() + 1).padStart(2, "0");
	const day = String(currentDate.getDate()).padStart(2, "0");
	return `${year}-${month}-${day}`;
};
*/

/**
 * Converts current date or given Date object to ISO date string in current timezone
 * Alters the date object given as a parameter
 * @param {Date} [d] - Date object
 * @returns {string} YYYY-MM-DD
 */
export const localDate = (d = new Date()) => {
	d.setMinutes(d.getMinutes() - d.getTimezoneOffset())
	return d.toISOString().slice(0, 10)
}

// react-select
export const kevaMapper = ([key, value]) => {
	if (key.startsWith('T')) {
		return {
			// Trafi
			value: key.slice(1), // remove starting T
			label: `${key} - ${value} (Trafi)`,
		}
	}
	return {
		// Keva
		value: key,
		label: `${key} - ${value}`,
	}
}

export const selectMapper = ([key, value]) => ({
	value: key,
	label: `${key} - ${value}`,
})

export function maxTwoDecimals(value) {
	const float = parseFloat(value)
	if (Number.isNaN(float)) return null
	return Math.round(float * 100) / 100
}

// https://stackoverflow.com/questions/17732897/difference-between-two-dates-in-years-months-days-in-javascript
export function dateDiff(startDate, endDate) {
	startDate = new Date(startDate)
	if (!endDate) {
		endDate = new Date()
	}
	if (startDate > endDate) {
		const swap = startDate
		startDate = endDate
		endDate = swap
	}
	const startYear = startDate.getFullYear()
	const february = (startYear % 4 === 0 && startYear % 100 !== 0) || startYear % 400 === 0 ? 29 : 28
	const daysInMonth = [31, february, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31]

	let yearDiff = endDate.getFullYear() - startYear

	let monthDiff = endDate.getMonth() - startDate.getMonth()
	if (monthDiff < 0) {
		--yearDiff
		monthDiff += 12
	}

	let dayDiff = endDate.getDate() - startDate.getDate()
	if (dayDiff < 0) {
		if (monthDiff > 0) {
			--monthDiff
		} else {
			--yearDiff
			monthDiff = 11
		}
		dayDiff += daysInMonth[startDate.getMonth()]
	}

	// Work in progress
	const hourDiff = endDate.getHours() - startDate.getHours()
	/*
	if (hourDiff < 0) {
		if (dayDiff > 0) {
			--dayDiff;
		} else {
			--monthDiff;
			dayDiff = daysInMonth[startDate.getMonth()] - 1;
		}
	}
*/

	return {
		years: yearDiff,
		months: monthDiff,
		days: dayDiff,
		hours: hourDiff,
	}
}

export function socSecToBirthDate(socSec) {
	if (!socSec || socSec.length < 7) {
		return null
	}
	const dayOfMonth = socSec.substring(0, 2)
	const month = socSec.substring(2, 4)
	let year = Number(socSec.substring(4, 6))
	const c = socSec.substring(6, 7) // century
	switch (c) {
		case '+':
			year += 1800
			break
		case '-':
			year += 1900
			break
		case 'A':
			year += 2000
			break
		default:
			return null
	}
	return new Date(`${year}-${month}-${dayOfMonth}`)
}

const whitespaceRE = /\s+/g
export const ibanFormat = (iban) => {
	const matches = iban?.replace(whitespaceRE, '').match(/.{1,4}/g)
	return matches ? matches.join(' ') : null
}
export const ibanFormatter = ({ value }) => ibanFormat(value)

export function formBIC(ibanString) {
	const iban = ibanString.replace(whitespaceRE, '') // remove whitespaces
	if (!iban) {
		return null
	}
	if (iban.slice(0, 2) !== 'FI') {
		return null
	}
	if (iban.slice(4, 5) === '1') {
		return 'NDEAFIHH'
	}
	if (iban.slice(4, 5) === '2') {
		return 'NDEAFIHH'
	}
	if (iban.slice(4, 5) === '5') {
		return 'OKOYFIHH'
	}
	if (iban.slice(4, 5) === '6') {
		return 'AABAFI22'
	}
	if (iban.slice(4, 5) === '8') {
		return 'DABAFIHH'
	}
	if (iban.slice(4, 6) === '31') {
		return 'HANDFIHH'
	}
	if (iban.slice(4, 6) === '33') {
		return 'ESSEFIHH'
	}
	if (iban.slice(4, 6) === '34') {
		return 'DABAFIHH'
	}
	if (iban.slice(4, 6) === '36') {
		return 'SBANFIHH'
	}
	if (iban.slice(4, 6) === '37') {
		return 'DNBAFIHX'
	}
	if (iban.slice(4, 6) === '38') {
		return 'SWEDFIHH'
	}
	if (iban.slice(4, 6) === '39') {
		return 'SBANFIHH'
	}
	if (iban.slice(4, 7) === '400') {
		return 'ITELFIHH'
	}
	if (iban.slice(4, 7) === '402') {
		return 'ITELFIHH'
	}
	if (iban.slice(4, 7) === '403') {
		return 'ITELFIHH'
	}
	if (iban.slice(4, 7) === '405') {
		return 'HELSFIHH'
	}
	if (iban.slice(4, 7) === '497') {
		return 'HELSFIHH'
	}
	if (iban.slice(4, 7) === '479') {
		return 'POPFFI22'
	}
	if (iban.slice(4, 7) === '713') {
		return 'CITIFIHX'
	}
	if (iban.slice(4, 7) * 1 >= 400 && iban.slice(4, 7) * 1 <= 408) {
		return 'ITELFIHH'
	}
	if (iban.slice(4, 7) * 1 >= 410 && iban.slice(4, 7) * 1 <= 412) {
		return 'ITELFIHH'
	}
	if (iban.slice(4, 7) * 1 >= 414 && iban.slice(4, 7) * 1 <= 421) {
		return 'ITELFIHH'
	}
	if (iban.slice(4, 7) * 1 >= 423 && iban.slice(4, 7) * 1 <= 432) {
		return 'ITELFIHH'
	}
	if (iban.slice(4, 7) * 1 >= 435 && iban.slice(4, 7) * 1 <= 452) {
		return 'ITELFIHH'
	}
	if (iban.slice(4, 7) * 1 >= 454 && iban.slice(4, 7) * 1 <= 464) {
		return 'ITELFIHH'
	}
	if (iban.slice(4, 7) * 1 >= 483 && iban.slice(4, 7) * 1 <= 493) {
		return 'ITELFIHH'
	}
	if (iban.slice(4, 7) * 1 >= 495 && iban.slice(4, 7) * 1 <= 496) {
		return 'ITELFIHH'
	}
	if (iban.slice(4, 7) * 1 >= 470 && iban.slice(4, 7) * 1 <= 478) {
		return 'POPFFI22'
	}
}

// returns next birthDay(Date) from given date
export function nextBirthDay(from, birthDate) {
	const fromDate = new Date(from)
	fromDate.setUTCHours(0, 0, 0, 0) // midnight
	const fromYear = fromDate.getFullYear()

	const nextDate = new Date(birthDate)
	nextDate.setFullYear(fromYear)
	if (nextDate < fromDate) {
		nextDate.setFullYear(fromYear + 1)
	}

	return nextDate
}

export const useInterval = (callback, delay) => {
	const savedCallback = useRef()

	useEffect(() => {
		savedCallback.current = callback
	}, [callback])

	useEffect(() => {
		function tick() {
			savedCallback.current()
		}
		if (delay !== null) {
			const id = setInterval(tick, delay)
			return () => clearInterval(id)
		}
	}, [delay])
}

export const blobToDataURL = (blob) =>
	new Promise((resolve, reject) => {
		const reader = new FileReader()
		reader.onload = () => resolve(reader.result)
		reader.onerror = (error) => reject(error)
		reader.readAsDataURL(blob)
	})

export const jsError = (...args) => (window.jsError ? window.jsError({ message: args }) : console.warn(...args))

export const tagFormatter = (tags) => tags?.map((tag) => tag.codeKey)

export async function fetchTags(setTagOptions) {
	const res = await tagManagerSearch() // parameters: keyPrefix, amount
	setTagOptions(
		res.data
			? res.data.map((tag) => ({
					value: tag.codeItemId, // tag.key
					label: tag.codeKey, // item.description
				}))
			: [],
	)
}

/**
 * Usage: [{name: "foo"}, {name: "bar"}].sort(sortByStringProp("name"))
 * @param {string} prop - A string property name to compare for
 * @returns {function} compareFn
 */
export function sortByStringProp(prop) {
	return (a, b) => {
		const propA = a[prop].toUpperCase() // ignore upper and lowercase
		const propB = b[prop].toUpperCase()
		if (propA < propB) return -1
		if (propA > propB) return 1
		return 0 // names must be equal
	}
}

export const bootstrapGridBreakpoints = {
	xs: 0,
	sm: '576px',
	md: '768px',
	lg: '992px',
	xl: '1200px',
	xxl: '1400px',
}

export async function logoFetch(url) {
	if (!url) return ''

	try {
		const res = await fetch(url, {
			mode: 'cors',
			cache: 'no-store',
		})
		const logoBlob = await res.blob()
		return await blobToDataURL(logoBlob)
	} catch (e) {
		jsError('logoFetch error', url, e)
		return ''
	}
}

//Käyttäjähallinnassa boolean / string muunnos
export const normalizeUsers = (users) => {
	return users.map((user) => {
		const converted = {}
		for (const [key, value] of Object.entries(user)) {
			if (value === 'true') converted[key] = true
			else if (value === 'false') converted[key] = false
			else converted[key] = value
		}
		return converted
	})
}

export const normalizeUser = (user) => {
	const converted = {}
	for (const [key, value] of Object.entries(user)) {
		if (value === 'true') converted[key] = true
		else if (value === 'false') converted[key] = false
		else converted[key] = value
	}
	return converted
}
